import React, { useCallback, useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Colors } from 'chart.js';
import { Doughnut, Pie } from 'react-chartjs-2';

import biService from '../../../service/bi.service';
import { BiReportType } from '../../../config/bi_report';
import DrillSelectionPath from './DrillSelectionPath';
import {
  BI_CHART_TOOLTIP_OPTIONS,
  createBiChartLabelTooltipPlugin,
  truncateBiChartLabel,
} from '../../../utils/biChartLabel';

ChartJS.register(ArcElement, Tooltip, Legend, Colors);

const formatChartValue = (value) =>
  new Intl.NumberFormat('hu-HU', { maximumFractionDigits: 20 }).format(value);

const spreadLabels = (labels, top, bottom, preferredGap) => {
  labels.sort((first, second) => first.targetY - second.targetY);

  const availableHeight = Math.max(0, bottom - top);
  const gap = labels.length > 1 ? Math.min(preferredGap, availableHeight / (labels.length - 1)) : preferredGap;

  labels.forEach((label, index) => {
    const previousY = labels[index - 1]?.y;
    label.y = Math.max(label.targetY, index === 0 ? top : previousY + gap);
  });

  for (let index = labels.length - 1; index >= 0; index -= 1) {
    const nextY = labels[index + 1]?.y;
    labels[index].y = Math.min(labels[index].y, index === labels.length - 1 ? bottom : nextY - gap);
  }
};

const spreadHorizontalLabels = (labels, left, right, preferredGap) => {
  labels.sort((first, second) => first.targetX - second.targetX);

  const totalWidth = labels.reduce((sum, label) => sum + label.width, 0);
  const availableGap = Math.max(0, right - left - totalWidth);
  const gap = labels.length > 1 ? Math.min(preferredGap, availableGap / (labels.length - 1)) : preferredGap;

  labels.forEach((label, index) => {
    const previousLabel = labels[index - 1];
    const minimumX = previousLabel
      ? previousLabel.x + previousLabel.width / 2 + label.width / 2 + gap
      : left + label.width / 2;
    label.x = Math.max(label.targetX, minimumX);
  });

  for (let index = labels.length - 1; index >= 0; index -= 1) {
    const nextLabel = labels[index + 1];
    const maximumX = nextLabel
      ? nextLabel.x - nextLabel.width / 2 - labels[index].width / 2 - gap
      : right - labels[index].width / 2;
    labels[index].x = Math.min(labels[index].x, maximumX);
  }
};

const chartValueLabelsPlugin = {
  id: 'biPieValueLabels',
  afterDatasetsDraw: (chart) => {
    const dataset = chart.data.datasets[0];
    if (!dataset?.showDataLabels) return;

    const meta = chart.getDatasetMeta(0);
    if (meta.hidden) return;

    const { ctx, chartArea } = chart;
    const fontSize = ChartJS.defaults.font.size;

    ctx.save();
    ctx.font = `${fontSize}px ${ChartJS.defaults.font.family}`;
    ctx.fillStyle = chart.options.color ?? ChartJS.defaults.color;
    ctx.strokeStyle = chart.options.color ?? ChartJS.defaults.color;
    ctx.lineWidth = 1;

    const labels = meta.data
      .map((arc, index) => {
        if (arc.hidden || !chart.getDataVisibility(index)) return null;

        const value = meta.controller.getParsed(index);
        if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;

        const angle = (arc.startAngle + arc.endAngle) / 2;
        const horizontalDirection = Math.cos(angle) >= 0 ? 1 : -1;
        const verticalDirection = Math.sin(angle) >= 0 ? 1 : -1;
        const isVerticalLabel = Math.abs(Math.sin(angle)) > Math.abs(Math.cos(angle));
        const position = isVerticalLabel
          ? verticalDirection > 0
            ? 'bottom'
            : 'top'
          : horizontalDirection > 0
            ? 'right'
            : 'left';
        const category = truncateBiChartLabel(chart.data.labels?.[index] ?? '', dataset.groupLabelMaxLength);
        const text = `${category}: ${formatChartValue(Number(value))}`;

        return {
          arc,
          angle,
          position,
          targetY: arc.y + Math.sin(angle) * (arc.outerRadius + 12),
          targetX: arc.x + Math.cos(angle) * (arc.outerRadius + 12),
          text,
          width: ctx.measureText(text).width,
        };
      })
      .filter(Boolean);

    const minimumY = Math.max(fontSize, chartArea.top - 30);
    const maximumY = Math.min(chart.height - fontSize, chartArea.bottom + 30);
    const preferredGap = fontSize + 5;
    spreadLabels(
      labels.filter((label) => label.position === 'left'),
      minimumY,
      maximumY,
      preferredGap
    );
    spreadLabels(
      labels.filter((label) => label.position === 'right'),
      minimumY,
      maximumY,
      preferredGap
    );
    const minimumX = Math.max(4, chartArea.left - 90);
    const maximumX = Math.min(chart.width - 4, chartArea.right + 90);
    spreadHorizontalLabels(
      labels.filter((label) => label.position === 'top'),
      minimumX,
      maximumX,
      8
    );
    spreadHorizontalLabels(
      labels.filter((label) => label.position === 'bottom'),
      minimumX,
      maximumX,
      8
    );

    labels.forEach(({ arc, angle, position, x, y, text }) => {
      const startX = arc.x + Math.cos(angle) * arc.outerRadius;
      const startY = arc.y + Math.sin(angle) * arc.outerRadius;

      ctx.beginPath();
      ctx.moveTo(startX, startY);

      if (position === 'top' || position === 'bottom') {
        const direction = position === 'bottom' ? 1 : -1;
        const routingY = arc.y + direction * (arc.outerRadius + 6);
        const endY = routingY + direction * 16;

        ctx.lineTo(startX, routingY);
        ctx.lineTo(x, endY);
        ctx.stroke();

        ctx.textAlign = 'center';
        ctx.textBaseline = position === 'bottom' ? 'top' : 'bottom';
        ctx.fillText(text, x, endY + direction * 4);
      } else {
        const direction = position === 'right' ? 1 : -1;
        const routingX = arc.x + direction * (arc.outerRadius + 6);
        const endX = routingX + direction * 16;

        ctx.lineTo(routingX, startY);
        ctx.lineTo(endX, y);
        ctx.stroke();

        ctx.textAlign = position === 'right' ? 'left' : 'right';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, endX + direction * 4, y);
      }
    });

    ctx.restore();
  },
};

const legendSpacingPlugin = {
  id: 'biPieLegendSpacing',
  beforeInit: (chart) => {
    if (!chart.legend) return;

    const originalFit = chart.legend.fit;
    chart.legend.fit = function fit() {
      originalFit.call(this);
      if (chart.data.datasets[0]?.showDataLabels) {
        this.height = Math.min(this.height + 40, this.maxHeight);
      }
    };
  },
};

const PieReport = ({ report, linkedReportIdState, activeLevelState, filterValsState, reportStatesState, height }) => {
  const [chartData, setChartData] = useState({});
  const [chartHeight, setChartHeight] = useState(650);

  const { activeLevel, setActiveLevel } = activeLevelState;
  const { filterVals, setFilterVals } = filterValsState;
  const { linkedReportId, setLinkedReportId } = linkedReportIdState;
  const { reportStates, setReportStates } = reportStatesState;
  const isDoughnut = report.reportType === BiReportType.DOUGHNUT;
  const ChartComponent = isDoughnut ? Doughnut : Pie;
  const generateChartLabels = ChartJS.overrides[isDoughnut ? 'doughnut' : 'pie'].plugins.legend.labels.generateLabels;

  const getChartData = useCallback(() => {
    biService.getReportData(report.id, activeLevel, filterVals).then((data) => setChartData(data));
  }, [report.id, activeLevel]);

  useEffect(() => {
    const prevReport = reportStates[reportStates.length - 1];
    if (Object.keys(report).length > 0 && prevReport?.reportId !== report.id) {
      getChartData();
    }
  }, [report, getChartData, activeLevel, reportStates]);

  useEffect(() => {
    if (height) setChartHeight(height);
  }, [height]);

  const hasDrillSelections = (filterVals?.filter ?? []).some((item) => !item?.isDefault);

  const handleResetDrill = useCallback(() => {
    setFilterVals((prev) => {
      const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
      return { ...prev, filter: defaultFilters };
    });
    setActiveLevel(null);
  }, [setFilterVals, setActiveLevel]);

  const handleSelectDrillLevel = useCallback(
    (targetIndex) => {
      setFilterVals((prev) => {
        const defaultFilters = (prev?.filter ?? []).filter((item) => item?.isDefault);
        const drillFilters = (prev?.filter ?? []).filter((item) => !item?.isDefault);
        const nextDrillFilters = drillFilters.slice(0, targetIndex + 1);
        return { ...prev, filter: [...defaultFilters, ...nextDrillFilters] };
      });
      setActiveLevel((prev) => (prev === null ? null : targetIndex));
    },
    [setFilterVals, setActiveLevel]
  );

  const valueField = report.biField?.find((field) => !field.isGroup && !field.isFilter);
  const groupLabelMaxLength = report.biField?.find((field) => field.isGroup)?.groupLabelMaxLength;
  const data = {
    labels: chartData.label,
    datasets: [
      {
        label: '',
        data: chartData.data,
        showDataLabels: valueField?.showDataLabels === true,
        groupLabelMaxLength,
        borderWidth: 1,
        hoverOffset: 4,
      },
    ],
  };
  const chartLabelTooltipPlugin = createBiChartLabelTooltipPlugin({
    maxLength: groupLabelMaxLength,
    useLegend: true,
  });

  const options = {
    maintainAspectRatio: false,
    aspectRatio: 1,
    layout: {
      padding: valueField?.showDataLabels ? { left: 110, right: 110, top: 10, bottom: 50 } : 0,
    },
    plugins: {
      tooltip: BI_CHART_TOOLTIP_OPTIONS,
      colors: {
        forceOverride: true,
      },
      title: {
        display: false,
        text: report.name,
      },
      legend: {
        labels: {
          generateLabels: (chart) =>
            generateChartLabels(chart).map((label) => ({
              ...label,
              text: truncateBiChartLabel(label.text, groupLabelMaxLength),
            })),
        },
      },
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const { biDrillingLink, biDrilling, biField, id } = report;

        const label = data.labels[elements[0].index];
        const maxLevel = biDrilling.length > 0 ? Math.max(...biDrilling.map((drill) => drill.level)) : null;
        let actLevel = activeLevel !== null ? activeLevel + 1 : 0;

        let filterField;

        if (actLevel === 0 || actLevel === null) {
          filterField = biField.find((f) => f.isGroup)?.field;
        } else {
          filterField = biDrilling?.find((f) => {
            return f.level === activeLevel;
          })?.field;
        }

        const newFilter = { ...filterVals };

        if (maxLevel + 1 === actLevel || (biDrillingLink && maxLevel === null)) {
          if (biDrillingLink) {
            setReportStates((prev) => {
              prev.push({
                reportId: id,
                linkedReportId: linkedReportId,
                filter: { filter: newFilter.filter.map((data) => data) },
                activeLevel,
              });
              return prev;
            });
            setLinkedReportId(biDrillingLink.linkedReport);

            newFilter.filter.push({ field: filterField, value: label });

            const updatedFilter = newFilter.filter.map((item) => {
              const newReportField = biDrillingLink.biDrillingLinkField.find(
                (linkField) => linkField.parentField === item.field
              );
              if (newReportField) {
                return { ...item, field: newReportField.childField };
              }
              return item;
            });

            setFilterVals({ filter: updatedFilter });
            setActiveLevel(null);
          } else {
            return;
          }
        } else {
          newFilter.filter.push({ field: filterField, value: label });
          if (!biDrilling.length > 0) {
            return;
          }
          setActiveLevel(actLevel);
          setFilterVals(newFilter);
        }
      }
    },
  };

  return (
    <>
      <div className="flex flex-col items-center mt-2">
        <button
          type="button"
          onClick={handleResetDrill}
          disabled={!hasDrillSelections}
          className={`${hasDrillSelections ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
        >
          <Typography variant="h6" sx={{ color: 'text.primary' }}>
            {report.name}
          </Typography>
        </button>
        <DrillSelectionPath filterVals={filterVals} onSelectLevel={handleSelectDrillLevel} />
      </div>
      <div className="mx-2 flex justify-center" style={{ height: `${chartHeight}px` }}>
        <ChartComponent
          data={data}
          options={options}
          plugins={[chartLabelTooltipPlugin, chartValueLabelsPlugin, legendSpacingPlugin]}
        />
      </div>
    </>
  );
};

export default PieReport;
